import { useContext, useEffect, useState } from 'react';
import ImageAdd from '../../../Assets/Add image.svg';
import { loadImage } from '../../../lib/utils/utils';
import { v4 as uuidv4 } from 'uuid';
import { InventoryEditProductFormContext } from '../../../Context/InventoryEditProductFormContext';
import { CropImageContext } from '../../../Context/CropImageContext';
import ProductImagePreview from './ProductImagePreview';
import CropImage from '../../../Components/CropImage/CropImage';
import { showToast } from '../../../Components/Toaster/Toaster';

function AddProductImage({ errorText }) {
    const { productImages, setProductImages } = useContext(InventoryEditProductFormContext);
    const { setImageToCrop, croppedImage, setCroppedImage, isImageCropped, setIsImageCropped, modalOpen, setModalOpen, imageId, setImageId, setAspectRatio } = useContext(CropImageContext);

    // Drag and drop state for reordering
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const handleImageSelection = async (e) => {
        const files = Array.from(e.target.files || []);
        const availableSlots = 5 - productImages.length;

        if (files.length > availableSlots) {
            showToast("You can only upload a maximum of 5 images.", "warning");
        }

        const filesToAdd = files.slice(0, availableSlots);

        for (const file of filesToAdd) {
            await processFile(file);
        }

        e.target.value = "";
    };

    const processFile = async (file) => {
        if (!file) return;

        const img = await loadImage(file);

        if (imageId) {
            const selectedImage = {
                id: imageId,
                url: img.src,
                file
            };
            const index = productImages.findIndex(item => item.id === imageId);
            if (index !== -1) {
                const updatedImages = [...productImages];
                updatedImages[index] = selectedImage;
                setProductImages(updatedImages);
            }
        } else {
            const selectedImage = {
                id: uuidv4(),
                url: img.src,
                file
            };
            setProductImages(prev => [...prev, selectedImage]);
        }
    }

    const handleDrop = async (e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files || []);
        const availableSlots = 5 - productImages.length;

        if (files.length > availableSlots) {
            alert("You can only upload a maximum of 5 images.");
        }

        const filesToAdd = files.slice(0, availableSlots);

        for (const file of filesToAdd) {
            await processFile(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    // Drag and drop handlers for reordering images
    const handleImageDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.target.parentNode);
        e.dataTransfer.setDragImage(e.target, 0, 0);
    };

    const handleImageDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        
        if (draggedIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleImageDragEnter = (e, index) => {
        e.preventDefault();
        if (draggedIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleImageDragLeave = (e) => {
        e.preventDefault();
        // Only clear dragOverIndex if we're actually leaving the image area
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;
        
        if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
            setDragOverIndex(null);
        }
    };

    const handleImageDrop = (e, dropIndex) => {
        e.preventDefault();
        
        if (draggedIndex === null || draggedIndex === dropIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const newImages = [...productImages];
        const draggedImage = newImages[draggedIndex];
        
        // Remove the dragged item
        newImages.splice(draggedIndex, 1);
        
        // Insert at the new position
        newImages.splice(dropIndex, 0, draggedImage);
        
        setProductImages(newImages);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const handleImageDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        if(modalOpen) {
            setModalVisible(true);
        } else {
            setModalVisible(false);
        }
    }, [modalOpen]);

    return (
        <div className="flex flex-col gap-4 border border-[#EAEAEA] p-5 rounded-xl">
            <div className="flex items-center justify-between">
                <h1 className='font-semibold text-[18px] leading-[26px]'>
                    Add Product Image &#40;Max 5&#41; <span className='text-base text-[#666666]'>&#40;1080px * 1296px&#41;<span className="text-[#DC2626]">*</span></span>
                </h1>

                {
                    errorText && 
                    <p className='text-[14px] leading-5 text-[#DC2626]'>{errorText}</p>
                }
            </div>
            

            <div 
                onDrop={handleDrop} 
                onDragOver={handleDragOver} 
                onDragLeave={handleDragLeave} 
                className="p-3 flex flex-wrap gap-3 rounded-xl bg-[#F5F5F5]"
            >
                {
                    productImages?.length < 5 && 
                    <label htmlFor='product-image' className="cursor-pointer border-dashed bg-[#E5E5E5] border-2 border-[#D1D5DB] w-[140px] h-[140px] rounded-lg flex flex-col items-center justify-center gap-2">
                        <div className="w-12 h-12 flex items-center justify-center">
                            <img src={ImageAdd} alt="" />
                        </div>

                        <p className='font-medium text-sm text-[#4B5563] w-[90%] text-center'>
                            <span className='text-[#4F46E5]'>Click to Upload</span> or Drag or drop
                        </p>
                    </label>
                }

                <input 
                    onChange={handleImageSelection} 
                    className='hidden'
                    type="file" 
                    accept="image/*" 
                    name="product-image" 
                    id="product-image" 
                    multiple
                />

                {productImages?.map((image, index) => {
                    const isDragging = draggedIndex === index;
                    const isDragOver = dragOverIndex === index;
                    
                    return (
                        <div
                            key={image.id}
                            draggable
                            onDragStart={(e) => handleImageDragStart(e, index)}
                            onDragOver={(e) => handleImageDragOver(e, index)}
                            onDragEnter={(e) => handleImageDragEnter(e, index)}
                            onDragLeave={handleImageDragLeave}
                            onDrop={(e) => handleImageDrop(e, index)}
                            onDragEnd={handleImageDragEnd}
                            className={`
                                transition-all duration-200 ease-in-out cursor-move
                                ${isDragging ? 'opacity-50 scale-95 rotate-2' : 'opacity-100 scale-100 rotate-0'}
                                ${isDragOver && !isDragging ? 'scale-105 ring-2 ring-blue-400 ring-offset-2' : ''}
                                hover:scale-102 relative
                            `}
                            style={{
                                transform: isDragging 
                                    ? 'scale(0.95) rotate(2deg)' 
                                    : isDragOver && !isDragging 
                                        ? 'scale(1.05)' 
                                        : 'scale(1) rotate(0deg)'
                            }}
                        >
                            {/* Visual indicator when dragging over */}
                            {isDragOver && !isDragging && (
                                <div className="absolute inset-0 bg-blue-100 border-2 border-dashed border-blue-400 rounded-lg z-10 flex items-center justify-center">
                                    <span className="text-blue-600 font-medium text-sm">Drop here</span>
                                </div>
                            )}
                            
                            <ProductImagePreview 
                                image={image} 
                            />
                        </div>
                    )
                })}
            </div>

            {
                modalOpen && 
                <div className={`${modalVisible ? 'opacity-100 visible bg-[#00000080]' : 'opacity-0 invisible'} transition-all duration-100 ease-in-out fixed z-50 w-full h-full top-0 left-0 flex justify-center items-center`}>
                    <CropImage />
                </div>
            }
        </div>
    );
}

export default AddProductImage;
